import React, { useState, useEffect, useCallback } from "react";
import { connect } from 'react-redux';
import { Dialog, withStyles } from '@material-ui/core';
import * as Yup from "yup";

import ColliForm from "./ColliForm";
import * as actions from './store/colliActions';
import * as warehouseActions from '../../sc-warehouses/Warehouse/store/warehouseActions';
import * as colliTypeActions from '../ColliType/store/colliTypeActions';
import * as productTypeActions from '../../sc-admin/ProductType/store/productTypeActions';
import History from "../../../History";
import { hasPermission } from "../../sc-utilities";
import { snackActions } from '../../sc-utilities/SnackBar';
import { parseURL } from "../../sc-utilities";
import moment from 'moment';
import { CancelToken } from 'axios';
import AppLogoLoading from '../../sc-logo';
import * as groupActions from "../../sc-group/Group/store/groupActions";


const styles = theme => ({
    submit: {
        backgroundColor: "transparent",
        boxShadow: "none",
        overflow: "hidden",
        alignItems: "center"
    },
    cancel: {
        width: "250px",
        margin: "10px"
    }
});

const ColliEdit = props => {
    const { classes, activeCustomer, activeWarehouseID, translations, permissions, onListCollies, onListWarehouses, onListColliTypes, onEditColli, onListGrades, onListProductTypes, onLoadColli, colli, onLoadColliInit, fetchGroups } = props;

    const [editData, setEditData] = useState({ collies: [], warehouses: [], colliTypes: [], loadingData: true, gradeIDs: [], productTypes: [] });
    const [editColi, setEditColi] = useState({ formattedGrades: [], gradeIDs: [], productTypes: [], formattedProductType: [] });
    const [groups, setGroups] = useState(null);

    let colliType = editData?.colliTypes.filter(colliType => colliType?.value === colli?.ColliTypeID);

    useEffect(() => {
        const requestTokenColliLoad = CancelToken.source();
        const requestTokenColli = CancelToken.source();
        const requestTokenWarehouse = CancelToken.source();
        const requestTokenTypes = CancelToken.source();
        const fetchData = async () => {
            // load colli
            await onLoadColli({ ActiveCustomerID: activeCustomer, ColliID: parseURL(window.location.href).ColliID }, { cancelToken: requestTokenColliLoad.token });
            let thisColli = await onListCollies({ listAll: true, Deleted: false, ActiveCustomerID: activeCustomer, q: { ColliID: parseURL(window.location.href).ColliID } }, { cancelToken: requestTokenColli.token });
            thisColli = thisColli[0];
            // list collies data
            let collies = await onListCollies({ listAll: true, Deleted: false, ActiveCustomerID: activeCustomer, q: { WarehouseID: activeWarehouseID, ColliTypeID: thisColli.holderColliTypes } }, { cancelToken: requestTokenColli.token });
            if (collies instanceof Array && collies.length < 1 && !thisColli.AllowHolderColliIDNull) {
                snackActions.snackBarComponent("No collies found, cannot select colli holder.", "info");
            }

            // list warehouses
            let warehouses = await onListWarehouses({ listAll: true, Deleted: false, ActiveCustomerID: activeCustomer }, { cancelToken: requestTokenWarehouse.token });
            if (warehouses instanceof Array && warehouses.length < 1) {
                snackActions.snackBarComponent("No warehouses found, please create at least one warehouse first.", "info");
            }

            //list colli types
            let colliTypes = await onListColliTypes({ listAll: true, Deleted: false, ActiveCustomerID: activeCustomer, q: { WarehouseID: activeWarehouseID } }, { cancelToken: requestTokenTypes.token });
            if (colliTypes instanceof Array && colliTypes.length < 1) {
                snackActions.snackBarComponent("No colli types found, please create at least one colli type first.", "info");
            }

            setEditData({ ...editData, collies: collies, colliTypes: colliTypes, warehouses: warehouses, loadingData: false });
        }
        fetchData();
        return () => {
            onLoadColliInit()
            requestTokenColliLoad.cancel('Operation canceled by the user.');
            requestTokenColli.cancel('Operation canceled by the user.');
            requestTokenWarehouse.cancel('Operation canceled by the user.');
            requestTokenTypes.cancel('Operation canceled by the user.');

        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const getGroups = async () => {
            const resultGroups = await fetchGroups({ ActiveCustomerID: activeCustomer });
            if (resultGroups) {
                let groupsToSet = resultGroups.data.map(group => ({ value: group.GroupID, displayValue: group.Name }));
                if (hasPermission(permissions, 'access_all_collies_on_customer')) {
                    groupsToSet = [...groupsToSet, { value: 'n/a', displayValue: 'n/a' }];
                }
                setGroups(groupsToSet);
            }
        }

        if (hasPermission(permissions, 'group_list')) {
            getGroups();
        }
    }, [fetchGroups, activeCustomer, permissions]);


    let holderColliValue =
        colli && editData.collies
            ? editData.collies.filter(displayValue => {
                return colli.HolderColliID === displayValue.value;
            })[0]
            : null;

    const formatGrade = useCallback(async (gradeIDs = [], grades = []) => {
        let gradeID = grades.map(grade => grade.GradeID);
        return gradeIDs.filter(grade => gradeID.includes(grade.value));
    }, [])
    const formatProductType = useCallback(async (productTypeIDs = [], productTypes = []) => {
        let productTypeID = productTypes.map(productType => productType.ProductTypeID);
        return productTypeIDs.filter(productType => productTypeID.includes(productType.value));
    }, [])


    const formatColi = useCallback(async (colli, requestTokenGrades, requestTokenProductTypes) => {
        let gradeIDs = await onListGrades({ ActiveCustomerID: activeCustomer }, { cancelToken: requestTokenGrades.token });
        if (gradeIDs instanceof Array && gradeIDs.length < 1) {
            snackActions.snackBarComponent("No grades found, please create at least one grade first.", "info");
        }
        let productTypes = await onListProductTypes({ ActiveCustomerID: activeCustomer }, { cancelToken: requestTokenProductTypes.token });
        if (productTypes instanceof Array && productTypes.length < 1) {
            snackActions.snackBarComponent("No product types found, please create at least one product types first.", "info");
        }
        let formattedGrades = await formatGrade(gradeIDs, colli.Grades);
        let formattedProductType = await formatProductType(productTypes, colli.ProductTypes);
        setEditColi({ ...editData, formattedGrades: formattedGrades, gradeIDs: gradeIDs, productTypes: productTypes, formattedProductType: formattedProductType });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        const requestTokenGrades = CancelToken.source();
        const requestTokenProductTypes = CancelToken.source();

        if (colli && Object.keys(colli).length > 0) {

            formatColi(colli, requestTokenGrades, requestTokenProductTypes);
        }
        return () => {
            requestTokenGrades.cancel('Operation canceled by the user.');
            requestTokenProductTypes.cancel('Operation canceled by the user.');

        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [colli]);

    const extractAllowedGrades = async selectedGradeIDs => {
        if (selectedGradeIDs instanceof Array && selectedGradeIDs.length === 0) {
            let data = await onListGrades({ ActiveCustomerID: activeCustomer });
            data = data.map(grade => grade.value);
            return data;
        }
        let data = selectedGradeIDs.map(allowedGradeIDs => allowedGradeIDs.value);
        return data;
    }

    const extractAllowedProductType = async selectedAllowedProductType => {
        if (selectedAllowedProductType instanceof Array && selectedAllowedProductType.length === 0) {
            let data = await onListProductTypes({ ActiveCustomerID: activeCustomer });
            data = data.map(allowedProductType => allowedProductType.value);
            return data;
        }
        let data = selectedAllowedProductType.map(allowedProductType => allowedProductType.value);
        return data;
    }


    const handleSubmit = async data => {
        let requestData = { ...data, ActiveCustomerID: activeCustomer, ColliID: parseURL(window.location.href).ColliID };
        requestData['GradeIDs'] = await extractAllowedGrades(requestData.GradeIDs);
        requestData['AllowedProductTypeIDs'] = await extractAllowedProductType(requestData.AllowedProductTypeIDs);
        requestData['HolderColliID'] = requestData.HolderColliID && requestData.HolderColliID.value ? requestData.HolderColliID.value : null;
        requestData['DateStarted'] = moment(requestData.DateStarted).format();
        requestData['ArrivedAt'] = moment(requestData.ArrivedAt).format();
        requestData['DateEnd'] = requestData.DateEnd ? moment(requestData.DateEnd).format() : null;
        requestData['Finished'] = requestData.Finished ? moment(requestData.Finished).format() : null;
        if (requestData['GroupID'] === 'n/a') {
            requestData['GroupID'] = null;
        }

        let result = await onEditColli(requestData);
        if (result) {
            History.goBack();
        }
    };

    const cancelHandler = () => {
        History.goBack();
    };

    let initialValues = {
        Name: colli && colli.Name ? colli.Name : '',
        Description: colli && colli.Description ? colli.Description : '',
        Locked: colli && colli.Locked ? colli.Locked : false,
        LockedType: colli && colli.LockedType ? colli.LockedType : "",
        Status: colli && colli.Status ? colli.Status : "Active",
        HolderColliID: holderColliValue ? holderColliValue : null,
        ArrivedAt: colli && colli.History && Array.isArray(colli.History) && colli.History.length > 0 ? colli.History[0].ArrivedAt : "",
        DateStarted: colli && colli.History && Array.isArray(colli.History) && colli.History.length > 0 ? colli.History[0].DateStarted : "",
        DateEnd: colli && colli.History && Array.isArray(colli.History) && colli.History.length > 0 ? colli.History[0].DateEnd : "",
        Finished: colli && colli.History && Array.isArray(colli.History) && colli.History.length > 0 ? colli.History[0].Finished : "",
        WarehouseID: colli && colli.WarehouseID ? colli.WarehouseID : "",
        ColliTypeID: colli && colli.ColliTypeID && editData.colliTypes.some(type => type.value === colli?.ColliTypeID) ? colli.ColliTypeID : '',
        GradeIDs: editColi.formattedGrades,
        AllowedProductTypeIDs: editColi.formattedProductType,
        AllowHolderColliIDNull: colli?.ColliType?.AllowHolderColliIDNull,
        GroupID: colli?.GroupID ?? 'n/a'
    };


    let formSettings = {
        Validation: {
            Locked: Yup.boolean(),
            Name: Yup.string().required(),
            Status: Yup.string().required(),
            WarehouseID: Yup.string().required(),
            ColliTypeID: Yup.string().required(),
            HolderColliID: Yup.object().when('AllowHolderColliIDNull', {
                is: value => typeof (value) === 'boolean' && (colli?.ColliType?.AllowHolderColliIDNull),
                then: Yup.object().nullable()
            }),
            ArrivedAt: Yup.date().required(),
            DateStarted: Yup.date().required(),
        },
        FormTitle: `${translations["edit_colli"] || "Edit colli"} (${colliType && colliType[0] && colliType[0].displayValue ? colliType[0].displayValue : ''})`,
        ButtonSubmit: hasPermission(permissions, 'colli_save') ? {
            Title: translations["save"] || "SAVE"
        } : null,
        ButtonCancel: {
            Title: translations["cancel"] || "Cancel",
            handleCancel: cancelHandler
        }
    };
    return !editData.loadingData ?
        <ColliForm
            formSettings={formSettings}
            initialValues={initialValues}
            handleSubmit={handleSubmit}
            qrCode={true}
            translations={translations}
            collies={editData.collies}
            warehouses={editData.warehouses}
            colliTypes={editData.colliTypes}
            gradeIDs={editColi.gradeIDs}
            productTypes={editColi.productTypes}
            ColliID={colli ? colli.ColliID : null}
            groups={groups}
        />
        :
        <Dialog disableBackdropClick={true} disableEscapeKeyDown={false} open={editData.loadingData}
            PaperProps={{
                classes: {
                    root: classes.submit
                }
            }}
        >

            <AppLogoLoading />
        </Dialog>
};

const mapStateToProps = state => {
    return {
        colli: state.logistic.colli.loaded,
        activeCustomer: state?.core?.login?.activeCustomer?.CustomerID ?? null,
        activeWarehouseID: state.core.login.user.ActiveWarehouseID,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        permissions: state?.core?.login?.permissions || []
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onLoadColliInit: data => dispatch(actions.loadColliInit(data)),
        onLoadColli: (data, options) => dispatch(actions.loadColli(data, options)),
        onEditColli: data => dispatch(actions.editColli(data)),
        onListCollies: (data, options) => dispatch(actions.listCollies(data, options)),
        onListWarehouses: (data, options) => dispatch(warehouseActions.listWarehouses(data, options)),
        onListColliTypes: (data, options) => dispatch(colliTypeActions.listColliTypes(data, options)),
        onListGrades: (data, options) => dispatch(actions.fetchGrades(data, options)),
        onListProductTypes: (data, options) => dispatch(productTypeActions.listProductTypes(data, options)),
        fetchGroups: params => dispatch(groupActions.fetchGroups(params))
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(ColliEdit));
